import React, { useCallback, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { getParticipantById, getParticipantDisplayName } from '../../../base/participants/functions';
import Popover from '../../../base/popover/components/Popover.web';
import Message from '../../../base/react/components/web/Message';
import { getFormattedTimestamp, getMessageText } from '../../functions';
import { handleLobbyChatInitialized } from "../../actions.any";
import { openChat } from "../../actions.web";
import { close as closeParticipantsPane } from "../../../participants-pane/actions.any";
const useStyles = makeStyles()((theme) => {
    return {
        chatMessageWrapper: {
            maxWidth: '100%'
        },
        chatMessage: {
            display: 'inline-flex',
            maxWidth: '100%',
            boxSizing: 'border-box',
            '&.error': {
                backgroundColor: theme.palette.actionDanger,
                borderRadius: 0,
                fontWeight: 100
            },
            '&.lobbymessage': {
                backgroundColor: theme.palette.support05
            }
        },
        sideBySideContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'left',
            alignItems: 'center',
            marginLeft: theme.spacing(1)
        },
        reactionBox: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            backgroundColor: theme.palette.grey[800],
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0, 1),
            cursor: 'pointer'
        },
        reactionCount: {
            fontSize: '0.8rem',
            color: theme.palette.grey[400]
        },
        replyButton: {
            padding: '2px',
            '&:not(:disabled)': {
                '&:hover': {
                    backgroundColor: 'transparent'
                },
                '&:active': {
                    backgroundColor: 'transparent'
                }
            },
        },
        replyWrapper: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            maxWidth: '100%'
        },
        messageContent: {
            maxWidth: '100%',
            overflow: 'hidden',
            flex: 1
        },
        optionsButtonContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1),
            minWidth: '32px',
            minHeight: '32px'
        },
        displayNameTimestampContainer: {
            display: 'flex',
            gap: '4px',
            '-webkit-tap-highlight-color': 'transparent',
        },
        displayName: {
            fontFamily: 'Lato Bold',
            fontWeight: 'normal',
            fontSize: '15px',
            lineHeight: '22px',
            color: 'rgba(255, 255, 255, 0.75)',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            '&.clickable': {
                cursor: 'pointer',
            }
        },
        userMessage: {
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            fontSize: '15px',
            lineHeight: '22px',
            color: 'rgba(255, 255, 255, 0.75)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
        },
        timestamp: {
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            fontSize: '12px',
            lineHeight: '15px',
            color: 'rgba(255, 255, 255, 0.2)',
            marginTop: '5px',
        },
        reactionsPopover: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.ui03,
            borderRadius: theme.shape.borderRadius,
            maxWidth: '150px',
            maxHeight: '400px',
            overflowY: 'auto',
            color: theme.palette.text01
        },
        reactionItem: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: theme.spacing(1),
            gap: theme.spacing(1),
            borderBottom: `1px solid ${theme.palette.common.white}`,
            paddingBottom: theme.spacing(1),
            '&:last-child': {
                borderBottom: 'none',
                paddingBottom: 0
            }
        },
        participantList: {
            marginLeft: theme.spacing(1),
            fontSize: '0.8rem',
            maxWidth: '120px'
        },
        participant: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    };
});
const ChatMessage = ({ message, state, showDisplayName, type, shouldDisplayChatMessageMenu, isCanSendPrivateMessages, knocking, t }) => {
    const { classes, cx } = useStyles();
    const [isHovered, setIsHovered] = useState(false);
    const [isReactionsOpen, setIsReactionsOpen] = useState(false);
    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);
    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);
    const handleReactionsOpen = useCallback(() => {
        setIsReactionsOpen(true);
    }, []);
    const handleReactionsClose = useCallback(() => {
        setIsReactionsOpen(false);
    }, []);
    /**
     * Renders the display name of the sender.
     *
     * @returns {React$Element<*>}
     */
    function _renderDisplayName() {
        const dispatch = useDispatch();
        const participant = useSelector((state) => getParticipantById(state, message.participantId));
        const isParticipantPaneOpen = useSelector((state) => state['features/participants-pane'].isOpen);
        const handleClick = useCallback(() => {
            if (!isCanSendPrivateMessages) {
                return;
            }
            if (message.lobbyChat) {
                dispatch(handleLobbyChatInitialized(message.participantId));
            }
            else {
                if (isParticipantPaneOpen) {
                    dispatch(closeParticipantsPane());
                }
                dispatch(openChat(participant));
            }
        }, []);
        return (React.createElement("div", { "aria-hidden": true, className: cx('display-name', classes.displayName, isCanSendPrivateMessages && 'clickable'), onClick: () => handleClick() }, message.displayName));
    }
    /**
     * Renders the time at which the message was sent.
     *
     * @returns {React$Element<*>}
     */
    function _renderTimestamp() {
        return (React.createElement("div", { className: cx('timestamp', classes.timestamp) }, getFormattedTimestamp(message)));
    }
    /**
     * Renders the reactions for the message.
     *
     * @returns {React$Element<*>}
     */
    const renderReactions = () => {
        if (!message.reactions || message.reactions.size === 0) {
            return null;
        }
        const reactionsArray = Array.from(message.reactions.entries())
            .map(([reaction, participants]) => {
            return {
                reaction,
                participants
            };
        })
            .sort((a, b) => b.participants.size - a.participants.size);
        const totalReactions = reactionsArray.reduce((sum, { participants }) => sum + participants.size, 0);
        const numReactionsDisplayed = 3;
        const reactionsContent = (React.createElement("div", { className: classes.reactionsPopover }, reactionsArray.map(({ reaction, participants }) => (React.createElement("div", { className: classes.reactionItem, key: reaction },
            React.createElement("span", null, reaction),
            React.createElement("span", null, participants.size),
            React.createElement("div", { className: classes.participantList }, Array.from(participants).map(participantId => (React.createElement("div", { className: classes.participant, key: participantId }, state && getParticipantDisplayName(state, participantId))))))))));
        return (React.createElement(Popover, { content: reactionsContent, onPopoverClose: handleReactionsClose, onPopoverOpen: handleReactionsOpen, position: 'top', trigger: 'hover', visible: isReactionsOpen },
            React.createElement("div", { className: classes.reactionBox },
                reactionsArray.slice(0, numReactionsDisplayed).map(({ reaction }, index) => React.createElement("span", { key: index }, reaction)),
                reactionsArray.length > numReactionsDisplayed && (React.createElement("span", { className: classes.reactionCount },
                    "+",
                    totalReactions - numReactionsDisplayed)))));
    };
    return (React.createElement("div", { className: cx(classes.chatMessageWrapper, type), id: message.messageId, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, tabIndex: -1 },
        React.createElement("div", { className: cx('chatmessage', classes.chatMessage, type, message.privateMessage && 'privatemessage', message.lobbyChat && !knocking && 'lobbymessage') },
            React.createElement("div", { className: classes.replyWrapper },
                React.createElement("div", { className: cx('messagecontent', classes.messageContent) },
                    showDisplayName && (React.createElement("div", { className: classes.displayNameTimestampContainer },
                        _renderDisplayName(),
                        _renderTimestamp())),
                    React.createElement("div", { className: cx('usermessage', classes.userMessage) },
                        React.createElement(Message, { text: getMessageText(message) })))))));
};
/**
 * Maps part of the Redux store to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {IProps}
 */
function _mapStateToProps(state, { message, isCanSendPrivateMessages }) {
    const { knocking } = state['features/lobby'];
    const localParticipantId = state['features/base/participants'].local?.id;
    return {
        shouldDisplayChatMessageMenu: isCanSendPrivateMessages && message.participantId !== localParticipantId,
        knocking,
        state
    };
}
export default translate(connect(_mapStateToProps)(ChatMessage));
